import { useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { READONLY } from '../lib/readonly';
import { useData } from '../lib/data';
import { useSelection } from '../lib/selection';
import { blockerKey } from '../lib/blockers';
import type { Blocker, Task } from '../lib/database.types';

// Blockers panel lives inside TaskView. Shows what a task is waiting on
// and lets you add/remove entries. Three flavors:
//   - task:     another task (picker), resolved to its title + clickable
//   - person:   free-text name + optional note
//   - external: free-text note (API key, vendor, event, etc.)
//
// All blockers are stored in tb_tasks.blocked_by as a typed jsonb array.
// The task's status is NOT auto-changed — the operator sets blocked/pending
// independently. This matches the behavior elsewhere in the board: the
// status field is intentional, blockers are context.
export function BlockersPanel({
  task,
  onChange,
}: {
  task: Task;
  onChange: (next: Blocker[]) => Promise<void>;
}) {
  const { allTasks } = useData();
  const { select } = useSelection();
  const [adding, setAdding] = useState<null | 'task' | 'person' | 'external'>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state for the three types
  const [taskQuery, setTaskQuery] = useState('');
  const [taskPick, setTaskPick] = useState<Task | null>(null);
  const [personName, setPersonName] = useState('');
  const [personNote, setPersonNote] = useState('');
  const [externalNote, setExternalNote] = useState('');

  const blockers = task.blocked_by;

  // Index tasks by id so we can render their current titles + completion
  // state. If a blocker points at a task that was deleted, we still show
  // the stored ID so the user can clean it up.
  const taskById = useMemo(() => {
    const m = new Map<string, Task>();
    for (const t of allTasks) m.set(t.id, t);
    return m;
  }, [allTasks]);

  const taskCandidates = useMemo(() => {
    const q = taskQuery.trim().toLowerCase();
    if (!q) return [];
    return allTasks
      .filter(
        (t) =>
          t.id !== task.id &&
          (t.title.toLowerCase().includes(q) || t.id.toLowerCase().includes(q)),
      )
      .slice(0, 8);
  }, [allTasks, taskQuery, task.id]);

  const reset = () => {
    setAdding(null);
    setTaskQuery('');
    setTaskPick(null);
    setPersonName('');
    setPersonNote('');
    setExternalNote('');
    setError(null);
  };

  const add = async (b: Blocker) => {
    if (READONLY) return;
    setSaving(true);
    setError(null);
    try {
      await onChange([...blockers, b]);
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (idx: number) => {
    if (READONLY) return;
    setSaving(true);
    setError(null);
    try {
      await onChange(blockers.filter((_, i) => i !== idx));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2 panel-hi p-3 rounded">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono text-dim uppercase tracking-widest">
          Blocked by ({blockers.length})
        </div>
        {!adding && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setAdding('task')}
              disabled={READONLY}
              className="text-[10px] font-mono text-amberHi hover:text-ink px-2 py-0.5 rounded border border-border hover:border-amber/60 transition-colors disabled:opacity-40"
            >
              + task
            </button>
            <button
              onClick={() => setAdding('person')}
              disabled={READONLY}
              className="text-[10px] font-mono text-amberHi hover:text-ink px-2 py-0.5 rounded border border-border hover:border-amber/60 transition-colors disabled:opacity-40"
            >
              + person
            </button>
            <button
              onClick={() => setAdding('external')}
              disabled={READONLY}
              className="text-[10px] font-mono text-amberHi hover:text-ink px-2 py-0.5 rounded border border-border hover:border-amber/60 transition-colors disabled:opacity-40"
            >
              + external
            </button>
          </div>
        )}
      </div>

      {blockers.length === 0 && !adding && (
        <div className="text-[11px] text-dim italic font-mono">nothing blocking</div>
      )}

      <ul className="space-y-1">
        {blockers.map((b, idx) => {
          const key = blockerKey(b, idx);
          if (b.type === 'task') {
            const linked = taskById.get(b.task_id);
            const done = linked?.status === 'completed' || linked?.status === 'cancelled';
            return (
              <li key={key} className="flex items-start gap-2 text-xs">
                <span className="text-dim font-mono mt-0.5">task</span>
                <button
                  onClick={() => linked && select({ type: 'task', id: b.task_id })}
                  disabled={!linked}
                  className={
                    'flex-1 text-left hover:text-amberHi transition-colors ' +
                    (done ? 'line-through text-dim' : '') +
                    (!linked ? ' text-red' : '')
                  }
                  title={linked ? 'open this task' : 'task not found (deleted?)'}
                >
                  {linked?.title ?? `${b.label ?? 'missing'} (${b.task_id.slice(0, 8)}…)`}
                </button>
                <button
                  onClick={() => remove(idx)}
                  disabled={READONLY || saving}
                  className="text-dim hover:text-red text-xs"
                  title="remove this blocker"
                >
                  ×
                </button>
              </li>
            );
          }
          if (b.type === 'person') {
            return (
              <li key={key} className="flex items-start gap-2 text-xs">
                <span className="text-dim font-mono mt-0.5">person</span>
                <div className="flex-1">
                  <span className="text-ink">{b.name}</span>
                  {b.note && <span className="text-dim font-mono"> · {b.note}</span>}
                </div>
                <button
                  onClick={() => remove(idx)}
                  disabled={READONLY || saving}
                  className="text-dim hover:text-red text-xs"
                >
                  ×
                </button>
              </li>
            );
          }
          return (
            <li key={key} className="flex items-start gap-2 text-xs">
              <span className="text-dim font-mono mt-0.5">external</span>
              <div className="flex-1 text-ink">{b.note}</div>
              <button
                onClick={() => remove(idx)}
                disabled={READONLY || saving}
                className="text-dim hover:text-red text-xs"
              >
                ×
              </button>
            </li>
          );
        })}
      </ul>

      {adding === 'task' && (
        <div className="space-y-2 pt-2 border-t border-border">
          <div className="text-[10px] font-mono text-dim">pick a task to wait on</div>
          <input
            autoFocus
            type="text"
            value={taskQuery}
            onChange={(e) => {
              setTaskQuery(e.target.value);
              setTaskPick(null);
            }}
            placeholder="search tasks by title…"
            className="w-full px-2 py-1.5 bg-bg border border-border rounded text-xs focus:outline-none focus:border-amber/60"
          />
          {taskCandidates.length > 0 && (
            <ul className="max-h-40 overflow-y-auto border border-border rounded divide-y divide-border">
              {taskCandidates.map((t) => {
                const isPicked = taskPick?.id === t.id;
                return (
                  <li key={t.id}>
                    <button
                      onClick={() => setTaskPick(t)}
                      className={
                        'w-full text-left px-2 py-1.5 text-xs hover:bg-panelHi ' +
                        (isPicked ? 'bg-amber/15' : '')
                      }
                    >
                      <div className="text-ink truncate">{t.title}</div>
                      <div className="text-[10px] text-dim font-mono">
                        {t.status} · {t.project_slug}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="flex items-center justify-end gap-2">
            <button onClick={reset} className="btn text-xs" disabled={saving}>
              cancel
            </button>
            <button
              onClick={() => taskPick && add({ type: 'task', task_id: taskPick.id })}
              disabled={!taskPick || saving}
              className="btn-primary text-xs"
            >
              {saving ? 'saving…' : 'add'}
            </button>
          </div>
        </div>
      )}

      {adding === 'person' && (
        <div className="space-y-2 pt-2 border-t border-border">
          <input
            autoFocus
            type="text"
            value={personName}
            onChange={(e) => setPersonName(e.target.value)}
            placeholder="person name (e.g. Rick Wong)"
            className="w-full px-2 py-1.5 bg-bg border border-border rounded text-xs focus:outline-none focus:border-amber/60"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && personName.trim()) {
                add({
                  type: 'person',
                  name: personName.trim(),
                  note: personNote.trim() || undefined,
                });
              }
            }}
          />
          <input
            type="text"
            value={personNote}
            onChange={(e) => setPersonNote(e.target.value)}
            placeholder="note (optional) — what are you waiting on from them?"
            className="w-full px-2 py-1.5 bg-bg border border-border rounded text-xs focus:outline-none focus:border-amber/60"
          />
          <div className="flex items-center justify-end gap-2">
            <button onClick={reset} className="btn text-xs" disabled={saving}>
              cancel
            </button>
            <button
              onClick={() =>
                personName.trim() &&
                add({
                  type: 'person',
                  name: personName.trim(),
                  note: personNote.trim() || undefined,
                })
              }
              disabled={!personName.trim() || saving}
              className="btn-primary text-xs"
            >
              {saving ? 'saving…' : 'add'}
            </button>
          </div>
        </div>
      )}

      {adding === 'external' && (
        <div className="space-y-2 pt-2 border-t border-border">
          <input
            autoFocus
            type="text"
            value={externalNote}
            onChange={(e) => setExternalNote(e.target.value)}
            placeholder="what's blocking this? (e.g. Pipedrive API key, vendor reply)"
            className="w-full px-2 py-1.5 bg-bg border border-border rounded text-xs focus:outline-none focus:border-amber/60"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && externalNote.trim()) {
                add({ type: 'external', note: externalNote.trim() });
              }
            }}
          />
          <div className="flex items-center justify-end gap-2">
            <button onClick={reset} className="btn text-xs" disabled={saving}>
              cancel
            </button>
            <button
              onClick={() =>
                externalNote.trim() && add({ type: 'external', note: externalNote.trim() })
              }
              disabled={!externalNote.trim() || saving}
              className="btn-primary text-xs"
            >
              {saving ? 'saving…' : 'add'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="text-xs font-mono text-red border border-red/40 bg-red/10 rounded p-2">
          {error}
        </div>
      )}
    </div>
  );
}

// Exported supabase update helper so TaskView can wire it with the
// optimistic local patch for the task row.
export async function writeBlockers(taskId: string, next: Blocker[]): Promise<void> {
  const { error } = await supabase
    .from('tb_tasks')
    .update({ blocked_by: next })
    .eq('id', taskId);
  if (error) throw new Error(error.message);
}
